<template>
  <DetailCard
    v-if="!isLoading"
    key="urun-bilgileri"
    title="Ürün Bilgileri"
    cardKey="product-infos"
    :showUpdateButton="true"
    :showApplyToVariants="true"
    :applyVariantsAllCheckbox="false"
    v-model:applyToVariants="localForm.applyToVariants"
    @requestUpdate="requestUpdate"
    @saveChanges="saveChanges"
    :isLoading="isLoading"
    :isSaving="isSaving"
    :isSaved="!hasChanges || showSuccessState"
    :showSuccessState="showSuccessState"
    :saveButtonWidth="showSuccessState ? 'auto' : '183px'"
    :cardClass="{ 'product-disabled': isProductDisabled }"
  >
    <div class="input-area">
      <div class="input-with-info d-flex flex-column">
        <Input
          id="productName"
          v-model:value="localForm.title"
          label="Ürün Adı"
          type="text"
          size="md"
          :disabled="true"
        />
      </div>

      <div class="input-with-info d-flex flex-column">
        <Input
          id="categoryName"
          v-model:value="localForm.category"
          label="Ürün Kategorisi"
          type="text"
          size="md"
          :disabled="true"
        />
      </div>

      <div class="input-with-info d-flex flex-column">
        <div class="d-flex">
          <Input
            id="modelCode"
            v-model:value="localForm.modelCode"
            :isError="!isModelCodeValid"
            :label="isModelCodeRequired ? 'Model Kodu*' : 'Model Kodu'"
            type="text"
            @change="validateModelCode"
            @focus="handleInputFocus"
            size="md"
            :disabled="isProductDisabled"
          />
          <div v-if="!isModelCodeValid" class="basic-input-icon">
            <TooltipPlugin :tooltipText="inputsErrorText.modelCode">
              <template #tooltip-btn>
                <Icons name="InfoCircle" color="#ff0000" size="sm" />
              </template>
            </TooltipPlugin>
          </div>
        </div>
        <span class="helper-text">
          Model kodunu değiştirirseniz, ürün grubuna bağlı diğer tüm varyantlara
          uygulanır.
        </span>
      </div>
    </div>
  </DetailCard>
  <ProductInformationsSkeleton v-else />
</template>

<script setup>
import { reactive, watch, ref, computed } from "vue";
import { useStore } from "vuex";
import DetailCard from "./DetailCard.vue";
import { Input, TooltipPlugin, Icons } from "@n11/so-ui-components";
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';
import ProductInformationsSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductInformationsSkeleton.vue'

const store = useStore();

// Props
const props = defineProps({
  productInfo: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits([
  "update:productInfo",
  "saveChanges",
  "requestUpdate",
  "hasChanges",
  "saved",
]);

const isModelCodeValid = ref(true);

const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const productDetail = computed(() => store.getters['products/getProductDetail']);

const isModelCodeRequired = computed(() => {
  const attributes = productDetail.value?.attributes || [];
  if (attributes.length === 0) {
    return true;
  }
  return attributes.some((att) => att.isN11Grouping === true);
});

const inputsErrorText = ref({
  modelCode: "Model Kodu zorunludur.",
});

const localForm = reactive({ ...props.productInfo });

const originalForm = ref({ ...props.productInfo });

const showSuccessState = ref(false);

const isSaving = computed(() => {
  const loading = store.getters['products/isProductModelCodeLoading'];
  return loading;
});

const hasChanges = computed(() => {
  return localForm.modelCode !== originalForm.value.modelCode;
});

watch(hasChanges, (changed) => {
  if (changed) emit('hasChanges');
});

watch(
  () => props.productInfo,
  (newValue) => {
    Object.assign(localForm, newValue);
    originalForm.value = { ...newValue };
  },
  { deep: true, immediate: true }
);

const validateModelCode = () => {
  if (!isModelCodeRequired.value) {
    isModelCodeValid.value = true;
    return true;
  }
  isModelCodeValid.value =
    localForm.modelCode && localForm.modelCode.length > 0;
  return isModelCodeValid.value;
};

const handleInputFocus = () => {
  isModelCodeValid.value = true;
  if (showSuccessState.value) {
    showSuccessState.value = false;
  }
};

const saveChanges = () => {
  if (!isModelCodeRequired.value) {
    emit("saveChanges", { ...localForm });
    return;
  }
  const isModelValid = validateModelCode();
  if (!isModelValid) {
    return;
  }
  emit("saveChanges", { ...localForm });
};

watch(isSaving, (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    const error = store.getters['products/getProductModelCodeError'];
    if (!error) {
      originalForm.value = { ...localForm };
      showSuccessState.value = true;
      sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
        button_name: 'Değişiklikleri Kaydet',
        button_type: 'Save Changes',
        button_area: 'Product Information',
        item_id: productDetail.value?.id,
      });
      emit('saved');
    }
  }
});

const requestUpdate = () => {
  emit("requestUpdate");
};

const getProductInformation = () => {
  const isValid = isModelCodeRequired.value ? validateModelCode() : true;
  return {
    isValid,
    data: {
      modelCode: localForm.modelCode || '',
    },
  };
};

const showSaveAllSuccessState = () => {
  originalForm.value = { ...localForm };
  showSuccessState.value = true;
};

defineExpose({
  getProductInformation,
  showSaveAllSuccessState,
});
</script>

<style scoped>
.input-area {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

}

.input-with-info {
  gap: 4px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.basic-input-icon {
  position: absolute;
  z-index: 1;
  right: 12px;
  top: 12px;
  cursor: pointer;
}

.helper-text {
  font-size: 11px;
  line-height: 14px;
  color: #8c8c8c;
  padding: 4px 12px 0;
}

.d-flex {
  display: flex;
}

.flex-column {
  flex-direction: column;
}

@media (max-width: 960px) {
  .input-area {
    grid-template-columns: 1fr;
  }
}
</style>
